import { fetchAvailableTimeSlots as fetchAvailableTimeSlotsFromFirebase } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { format, isAfter, formatISO } from 'date-fns';
import { queryClient, queryKeys } from './queryClient';
import { verificarConflito } from './recentBookingsCache';
import { 
  checkTimeSlotAvailability as checkTimeSlotAvailabilityFromFirebase,
  getTimeBlockings as getTimeBlockingsFromFirebase
} from '../firebase/appointments';

/**
 * Obtém o salonId de diversas fontes possíveis
 * 
 * @returns {string|null} - ID do salão ou null se não encontrado
 */
export const getSalonId = () => {
  // Tentar obter da URL (padrões comuns)
  const urlMatches = [
    // Formato: /salons/{salonId}/...
    window.location.pathname.match(/\/salons\/([^\/]+)/),
    // Formato: /salon/{salonId}/...
    window.location.pathname.match(/\/salon\/([^\/]+)/),
    // Formato: /admin/{salonId}/...
    window.location.pathname.match(/\/admin\/([^\/]+)\//)
  ];
  
  for (const match of urlMatches) {
    if (match && match[1]) {
      return match[1];
    }
  }
  
  // Tentar obter de variáveis globais
  if (window.salonId) {
    return window.salonId;
  }
  
  if (window.salon && window.salon.id) {
    return window.salon.id;
  }
  
  // Tentar obter do localStorage
  if (localStorage.getItem('salonId')) {
    return localStorage.getItem('salonId');
  }
  
  console.warn('[getSalonId] Não foi possível determinar salonId! Usando fallback.');
  return null;
};

/**
 * Busca horários disponíveis para uma data, profissional e serviços específicos
 * 
 * @param {string} salonId - ID do salão
 * @param {Date} date - Data selecionada
 * @param {Array} services - Lista de serviços selecionados
 * @returns {Promise<Array>} - Lista de horários disponíveis
 */
export const getAvailableTimeSlots = async (salonId, date, services, excludeAppointmentId = null) => {
  // Log para verificar como os serviços estão sendo recebidos
  console.log('[availability.js] getAvailableTimeSlots - serviços recebidos:', services);
  console.log('[availability.js] getAvailableTimeSlots - tipo dos serviços:', typeof services, Array.isArray(services));
  console.log('[availability.js] getAvailableTimeSlots - excludeAppointmentId:', excludeAppointmentId);
  
  // Usar a função getSalonId como fallback se salonId for indefinido
  const effectiveSalonId = salonId || getSalonId();
  
  // Tratamento de serviços para garantir que sempre temos algo válido
  let normalizedServices = services;
  
  // Se services for nulo ou indefinido, criar array vazio
  if (!normalizedServices) {
    normalizedServices = [];
  } 
  // Se não for array, converter para array
  else if (!Array.isArray(normalizedServices)) {
    normalizedServices = [normalizedServices];
  }
  
  console.log('[availability.js] getAvailableTimeSlots - serviços normalizados:', normalizedServices);
  console.log('[availability.js] getAvailableTimeSlots - salonId efetivo:', effectiveSalonId);
  
  // Verificação de condições necessárias
  if (!effectiveSalonId || !date || normalizedServices.length === 0) {
    console.log('[availability.js] getAvailableTimeSlots - condição de retorno antecipado:', {
      effectiveSalonId,
      date,
      normalizedServices,
      length: normalizedServices.length
    });
    return [];
  }

  try {
    // Formatar a data para o formato esperado pela API
    const formattedDate = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd');
    console.log('[availability.js] getAvailableTimeSlots - data formatada:', formattedDate);
    
    // Obter os IDs dos serviços
    const serviceIds = normalizedServices.map(service => 
      typeof service === 'object' ? service.id : service
    );
    
    console.log('[availability.js] getAvailableTimeSlots - serviceIds extraídos:', serviceIds);
    
    // Verificar se já temos os dados no cache do React Query
    const cacheKey = queryKeys.availability.byServiceAndDate(effectiveSalonId, serviceIds, formattedDate, excludeAppointmentId);
    const cachedData = queryClient.getQueryData(cacheKey);
    
    if (cachedData) {
      console.log('[availability.js] getAvailableTimeSlots - usando dados em cache, quantidade:', cachedData.length);
      return cachedData;
    }
    
    // Obter todos os profissionais disponíveis para estes serviços
    // Usando o cache do React Query para evitar chamadas redundantes
    const professionalsKey = queryKeys.professionals.byService(effectiveSalonId, serviceIds[0]);
    let professionals = queryClient.getQueryData(professionalsKey);
    
    if (!professionals) {
      console.log('[availability.js] getAvailableTimeSlots - buscando profissionais para o serviço', serviceIds[0]);
      professionals = await getProfessionals(effectiveSalonId, serviceIds);
      // Armazenar no cache
      queryClient.setQueryData(professionalsKey, professionals);
    }
    
    if (!professionals || professionals.length === 0) {
      console.log('[availability.js] getAvailableTimeSlots - nenhum profissional encontrado para os serviços');
      queryClient.setQueryData(cacheKey, []);
      return [];
    }
    
    console.log('[availability.js] getAvailableTimeSlots - profissionais disponíveis:', professionals.length);
    
    // Obter disponibilidade para o primeiro profissional
    // Posteriormente podemos implementar uma lógica mais complexa para escolher o profissional
    const firstProfessionalId = professionals[0].id;
    console.log('[availability.js] getAvailableTimeSlots - usando profissional:', firstProfessionalId);
    
    // Buscar os slots disponíveis
    
    // Criar chave de consulta específica para este profissional e data
    const specificCacheKey = queryKeys.professionals.availability(effectiveSalonId, firstProfessionalId, formattedDate);
    
    // Verificar se já temos resultados específicos em cache
    let timeSlots = queryClient.getQueryData(specificCacheKey);
    
    if (!timeSlots) {
      console.log('[availability.js] getAvailableTimeSlots - buscando slots do Firebase para', formattedDate, 'com excludeAppointmentId:', excludeAppointmentId);
      timeSlots = await fetchAvailableTimeSlotsFromFirebase(
        effectiveSalonId,
        formattedDate,
        firstProfessionalId,
        serviceIds,
        excludeAppointmentId
      );
      
      // Armazenar em ambos os caches para consultas futuras
      queryClient.setQueryData(specificCacheKey, timeSlots);
    }
    
    console.log('[availability.js] getAvailableTimeSlots - slots obtidos:', timeSlots?.length || 0);
    
    // Verificar conflitos com reservas temporárias
    const timeSlotsWithConflicts = timeSlots.map(slot => {
      // Se o slot já for um objeto com propriedades
      if (typeof slot === 'object') {
        const slotTime = slot.time;
        const { hasConflict, allowsOverlap } = verificarConflito(
          effectiveSalonId, 
          formattedDate, 
          slotTime, 
          firstProfessionalId
        );
        
        return {
          ...slot,
          hasConflicts: hasConflict,
          allowsOverlap: allowsOverlap || slot.allowsOverlap || false,
          available: slot.available && (!hasConflict || allowsOverlap)
        };
      }
      
      // Se o slot for apenas uma string (formato antigo)
      const { hasConflict, allowsOverlap } = verificarConflito(
        effectiveSalonId, 
        formattedDate, 
        slot, 
        firstProfessionalId
      );
      
      if (hasConflict) {
        return {
          time: slot,
          available: allowsOverlap,
          hasConflicts: true,
          allowsOverlap: allowsOverlap || false
        };
      }
      
      return slot;
    });
    
    // Filtrar slots que já passaram (se a data for hoje)
    const now = new Date();
    const isToday = now.toDateString() === (date instanceof Date ? date.toDateString() : null);
    
    let filteredSlots;
    
    if (isToday) {
      filteredSlots = timeSlotsWithConflicts
        .filter(slot => {
          const slotTime = typeof slot === 'object' ? slot.time : slot;
          const [hours, minutes] = slotTime.split(':').map(Number);
          const slotDate = new Date(date);
          slotDate.setHours(hours, minutes, 0, 0);
          return isAfter(slotDate, now);
        })
        .filter(slot => {
          if (typeof slot === 'object') {
            return slot.available;
          }
          return true; // Se for string, já foi filtrado anteriormente
        });
    } else {
      // Retornar apenas os horários disponíveis, considerando conflitos
      filteredSlots = timeSlotsWithConflicts
        .filter(slot => {
          if (typeof slot === 'object') {
            return slot.available;
          }
          return true; // Se for string, já foi filtrado anteriormente
        });
    }
    
    console.log('[availability.js] getAvailableTimeSlots - slots filtrados finais:', filteredSlots.length);
    
    // Salvar no cache do React Query para reutilização
    queryClient.setQueryData(cacheKey, filteredSlots);
    
    return filteredSlots;
  } catch (error) {
    console.error('[availability.js] Erro ao buscar horários disponíveis:', error);
    // Em caso de erro, retornar alguns horários fictícios em modo de desenvolvimento
    if (process.env.NODE_ENV !== 'production') {
      console.log('[availability.js] getAvailableTimeSlots - retornando horários fictícios em modo de desenvolvimento');
      return ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'];
    }
    return [];
  }
};

/**
 * Busca profissionais disponíveis para uma data, horário e serviços específicos
 * 
 * @param {string} salonId - ID do salão
 * @param {Date} date - Data selecionada
 * @param {string} time - Horário selecionado (formato HH:MM)
 * @param {Array} services - Lista de serviços selecionados
 * @returns {Promise<Array>} - Lista de profissionais disponíveis
 */
export const getAvailableProfessionals = async (salonId, date, time, services) => {
  // Usar a função getSalonId como fallback se salonId for indefinido
  const effectiveSalonId = salonId || getSalonId();
  
  if (!effectiveSalonId || !date || !time || !services || !Array.isArray(services) || services.length === 0) {
    // Em modo de desenvolvimento, retornar dados fictícios
    if (process.env.NODE_ENV !== 'production') {
      return [
        { id: 'prof1', nome: 'Profissional 1', foto: null },
        { id: 'prof2', nome: 'Profissional 2', foto: null }
      ];
    }
    
    return [];
  }

  try {
    // Formatar a data para o formato esperado pela API
    const formattedDate = format(date, 'yyyy-MM-dd');
    
    // Obter os IDs dos serviços
    const serviceIds = services.map(service => 
      typeof service === 'object' ? service.id : service
    );
    
    // Calcular a duração total dos serviços selecionados
    let serviceDuration = 0;
    
    // Verificar se temos objetos completos de serviços ou apenas IDs
    const hasCompleteServices = services.some(service => typeof service === 'object' && service.duracao);
    
    if (hasCompleteServices) {
      // Se temos objetos completos, calcular diretamente
      serviceDuration = services.reduce((total, service) => {
        return total + (typeof service === 'object' ? (service.duracao || 30) : 30);
      }, 0);
    } else {
      // Se temos apenas IDs, precisamos buscar informações detalhadas dos serviços
      const servicesRef = collection(db, `salons/${effectiveSalonId}/services`);
      const servicesSnapshot = await getDocs(servicesRef);
      const servicesData = servicesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filtrar apenas os serviços selecionados
      const selectedServices = servicesData.filter(s => serviceIds.includes(s.id));
      serviceDuration = selectedServices.reduce((total, service) => total + (service.duracao || 30), 0);
    }
    
    // Criar chave de cache específica para esta consulta, incluindo a duração
    const cacheKey = [...queryKeys.availability.professionalsByServiceAndDateTime(
      effectiveSalonId, 
      serviceIds,
      formattedDate,
      time
    ), serviceDuration]; // Adicionar duração à chave de cache
    
    // Verificar se já temos dados em cache
    const cachedData = queryClient.getQueryData(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    // Buscar todos os profissionais disponíveis para os serviços
    const professionalsKey = queryKeys.professionals.byService(effectiveSalonId, serviceIds[0]);
    let allProfessionals = queryClient.getQueryData(professionalsKey);
    
    if (!allProfessionals) {
      allProfessionals = await getProfessionals(effectiveSalonId, serviceIds);
      queryClient.setQueryData(professionalsKey, allProfessionals);
    }
    
    if (!allProfessionals || allProfessionals.length === 0) {
      // Em modo de desenvolvimento, retornar dados fictícios
      if (process.env.NODE_ENV !== 'production') {
        const mockData = [
          { id: 'prof1', nome: 'Profissional 1', foto: null },
          { id: 'prof2', nome: 'Profissional 2', foto: null }
        ];
        queryClient.setQueryData(cacheKey, mockData);
        return mockData;
      }
      
      queryClient.setQueryData(cacheKey, []);
      return [];
    }
    
    // Buscar bloqueios de horário para esta data
    const timeBlockings = await getTimeBlockings(effectiveSalonId, formattedDate);
    
    // Converter hora de início para minutos
    const [hours, minutes] = time.split(':').map(Number);
    const timeInMinutes = hours * 60 + minutes;
    
    // Calcular hora de término com base na duração dos serviços
    const endTimeInMinutes = timeInMinutes + serviceDuration;
    const endTimeStr = `${Math.floor(endTimeInMinutes / 60).toString().padStart(2, '0')}:${(endTimeInMinutes % 60).toString().padStart(2, '0')}`;
    
    // Verificar disponibilidade para cada profissional
    const availableProfessionals = [];
    const checkPromises = [];
    
    for (const professional of allProfessionals) {
      // Criar chave de cache específica para este profissional, incluindo duração
      const professionalCacheKey = [...queryKeys.professionals.availability(
        effectiveSalonId, 
        professional.id, 
        `${formattedDate}_${time}`
      ), serviceDuration]; // Adicionar duração à chave de cache
      
      // Verificar cache específico do profissional
      const cachedAvailability = queryClient.getQueryData(professionalCacheKey);
      
      if (cachedAvailability !== undefined) {
        if (cachedAvailability === true) {
          // Verificar também se há conflito com reservas temporárias
          const { hasConflict, allowsOverlap } = verificarConflito(
            effectiveSalonId, 
            formattedDate, 
            time, 
            professional.id
          );
          
          // Se não há conflito ou o profissional permite sobreposição
          if (!hasConflict || allowsOverlap) {
            availableProfessionals.push({
              ...professional,
              hasConflicts: hasConflict,
              allowsOverlap: allowsOverlap
            });
          }
        }
        continue;
      }
      
      // Se não estiver em cache, verificar disponibilidade
      const checkPromise = (async () => {
        try {
          // Obter slots disponíveis para este profissional
          const slots = await fetchAvailableTimeSlotsFromFirebase(
            effectiveSalonId,
            formattedDate,
            professional.id,
            serviceIds
          );
          
          // Verificar se o horário selecionado está disponível
          const isTimeSlotAvailable = slots.some(slot => 
            slot.time === time && slot.available
          );
          
          // Verificar se o horário está bloqueado por um timeBlocking
          const relevantBlockings = timeBlockings.filter(block => 
            block.profissionalId === null || block.profissionalId === professional.id
          );
          
          // Verificar se o intervalo completo (início + duração) está bloqueado
          const hasTimeBlockingConflict = relevantBlockings.some(blocking => {
            // Converter horários de bloqueio para minutos
            const blockStartMinutes = (blocking.horarioInicio.split(':').map(Number)[0] * 60) + 
                                     blocking.horarioInicio.split(':').map(Number)[1];
            const blockEndMinutes = (blocking.horarioFim.split(':').map(Number)[0] * 60) + 
                                   blocking.horarioFim.split(':').map(Number)[1];
            
            // Verificar sobreposição entre o intervalo do serviço e o bloqueio
            return (timeInMinutes < blockEndMinutes && endTimeInMinutes > blockStartMinutes);
          });
          
          // O profissional só está disponível se o slot inicial estiver disponível 
          // E o intervalo completo não colidir com nenhum bloqueio
          const isAvailable = isTimeSlotAvailable && !hasTimeBlockingConflict;
          
          // Verificar também se o horário de término não ultrapassa o horário de funcionamento
          // Isso pode ser expandido para verificar horários de funcionamento específicos 
          
          // Armazenar resultado no cache
          queryClient.setQueryData(professionalCacheKey, isAvailable);
          
          if (isAvailable) {
            // Verificar também se há conflito com reservas temporárias
            const { hasConflict, allowsOverlap } = verificarConflito(
              effectiveSalonId, 
              formattedDate, 
              time, 
              professional.id
            );
            
            // Verificar se o horário de término não conflita com reservas temporárias
            // Isso exigiria uma verificação adicional em todos os horários no intervalo
            
            // Se não há conflito ou o profissional permite sobreposição
            if (!hasConflict || allowsOverlap) {
              availableProfessionals.push({
                ...professional,
                hasConflicts: hasConflict,
                allowsOverlap: allowsOverlap,
                endTime: endTimeStr // Adicionar o horário de término para referência
              });
            }
          }
        } catch (error) {
          console.error(`[availability.js] Erro ao verificar disponibilidade do profissional ${professional.id}:`, error);
        }
      })();
      
      checkPromises.push(checkPromise);
    }
    
    // Aguardar todas as verificações
    await Promise.all(checkPromises);
    
    // Salvar no cache do React Query
    queryClient.setQueryData(cacheKey, availableProfessionals);
    
    // Se nenhum profissional estiver disponível em ambiente de desenvolvimento, retornar dados fictícios
    if (availableProfessionals.length === 0 && process.env.NODE_ENV !== 'production') {
      const mockData = [
        { id: 'prof1', nome: 'Profissional 1 (Fictício)', foto: null },
        { id: 'prof2', nome: 'Profissional 2 (Fictício)', foto: null }
      ];
      queryClient.setQueryData(cacheKey, mockData);
      return mockData;
    }
    
    return availableProfessionals;
  } catch (error) {
    console.error('[availability.js] Erro ao buscar profissionais disponíveis:', error);
    
    // Em caso de erro em ambiente de desenvolvimento, retornar dados fictícios
    if (process.env.NODE_ENV !== 'production') {
      return [
        { id: 'prof1', nome: 'Profissional 1 (Fictício)', foto: null },
        { id: 'prof2', nome: 'Profissional 2 (Fictício)', foto: null }
      ];
    }
    
    return [];
  }
};

/**
 * Obtém todos os profissionais que podem realizar os serviços selecionados
 * 
 * @param {string} salonId - ID do salão
 * @param {Array} serviceIds - IDs dos serviços selecionados
 * @returns {Promise<Array>} - Lista de profissionais
 */
async function getProfessionals(salonId, serviceIds = []) {
  // Usar a função getSalonId como fallback se salonId for indefinido
  const effectiveSalonId = salonId || getSalonId();
  
  if (!effectiveSalonId) {
    console.error('[getProfessionals] salonId é obrigatório');
    return [];
  }
  
  try {
    // Verificar se já temos os dados em cache
    const cacheKey = serviceIds.length > 0 
      ? queryKeys.professionals.byService(effectiveSalonId, serviceIds[0])
      : queryKeys.professionals.all(effectiveSalonId);
    
    const cachedProfessionals = queryClient.getQueryData(cacheKey);
    if (cachedProfessionals) {
      return cachedProfessionals;
    }
    
    // Buscar todos os profissionais do salão
    const professionalsRef = collection(db, `salons/${effectiveSalonId}/professionals`);
    const snapshot = await getDocs(professionalsRef);
    
    const professionals = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Armazenar todos os profissionais em cache
    queryClient.setQueryData(queryKeys.professionals.all(effectiveSalonId), professionals);
    
    // Se não há serviços selecionados, retornar todos os profissionais
    if (!serviceIds || serviceIds.length === 0) {
      return professionals;
    }
    
    // Filtrar profissionais que podem realizar todos os serviços selecionados
    const filteredProfessionals = professionals.filter(professional => {
      // Se o profissional tem um array de serviços, verificar se contém todos os selecionados
      if (professional.services && Array.isArray(professional.services)) {
        return serviceIds.every(serviceId => 
          professional.services.includes(serviceId)
        );
      }
      
      // Se não tem lista de serviços, assumir que pode realizar todos
      return true;
    });
    
    // Armazenar profissionais filtrados em cache
    queryClient.setQueryData(cacheKey, filteredProfessionals);
    
    return filteredProfessionals;
  } catch (error) {
    console.error('[availability.js] Erro ao buscar profissionais:', error);
    return [];
  }
}

/**
 * Função para verificar a disponibilidade de um horário específico
 * @param {string} salonId - ID do salão
 * @param {string} date - Data no formato YYYY-MM-DD
 * @param {string} time - Horário no formato HH:MM
 * @param {string} professionalId - ID do profissional
 * @param {Array} serviceIds - IDs dos serviços
 * @param {string} excludeAppointmentId - ID opcional do agendamento a ser excluído da verificação
 * @returns {Promise<Object>} - Resultado com disponibilidade e conflitos
 */
export const checkTimeSlotAvailability = async (
  salonId,
  date,
  time,
  professionalId,
  serviceIds,
  excludeAppointmentId = null
) => {
  // Usar a função getSalonId como fallback se salonId for indefinido
  const effectiveSalonId = salonId || getSalonId();
  
  if (!effectiveSalonId) {
    console.error('[checkTimeSlotAvailability] salonId é obrigatório');
    return {
      available: false,
      conflicts: [{ type: 'error', message: 'ID do salão não foi especificado' }],
      hasAppointmentConflicts: false,
      hasBlockingConflicts: false,
      allowsOverlap: false
    };
  }
  
  try {
    // Verificar cache para este slot específico
    const cacheKey = queryKeys.availability.slotStatus(
      effectiveSalonId, 
      date, 
      time, 
      professionalId, 
      Array.isArray(serviceIds) ? serviceIds : [],
      excludeAppointmentId
    );
    
    const cachedData = queryClient.getQueryData(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    // Verificar também conflitos temporários no cache de reservas
    const { hasConflict, allowsOverlap } = verificarConflito(
      effectiveSalonId, 
      date, 
      time, 
      professionalId
    );
    
    // Se há conflito temporário e não permite sobreposição, retornar indisponível
    if (hasConflict && !allowsOverlap) {
      const result = {
        available: false,
        conflicts: [{ 
          type: 'temporaryBooking', 
          message: 'Horário temporariamente reservado'
        }],
        hasAppointmentConflicts: true,
        hasBlockingConflicts: false,
        allowsOverlap: false
      };
      
      // Salvar em cache
      queryClient.setQueryData(cacheKey, result);
      return result;
    }
    
    // Delegamos para a implementação no Firebase
    const result = await checkTimeSlotAvailabilityFromFirebase(
      effectiveSalonId,
      date,
      time,
      professionalId,
      serviceIds,
      excludeAppointmentId
    );
    
    // Se não há conflito no Firebase, mas há conflito temporário
    // e este conflito permite sobreposição
    if (result.available && hasConflict && allowsOverlap) {
      result.hasAppointmentConflicts = true;
      result.conflicts.push({ 
        type: 'temporaryBooking', 
        message: 'Horário temporariamente reservado, mas permite sobreposição'
      });
      // Ainda está disponível porque allowsOverlap é true
    }
    
    // Salvar em cache
    queryClient.setQueryData(cacheKey, result);
    
    return result;
  } catch (error) {
    console.error('[availability.js] Erro ao verificar disponibilidade do slot:', error);
    
    // Retornar resultado de erro
    return {
      available: false,
      conflicts: [{ type: 'error', message: 'Erro ao verificar disponibilidade' }],
      hasAppointmentConflicts: false,
      hasBlockingConflicts: false,
      allowsOverlap: false,
      error: error.message
    };
  }
};

/**
 * Função para buscar bloqueios de horário para uma data específica
 * @param {string} salonId - ID do salão
 * @param {string} date - Data no formato YYYY-MM-DD
 * @param {string} professionalId - ID opcional do profissional
 * @returns {Promise<Array>} - Lista de bloqueios
 */
export const getTimeBlockings = async (salonId, date, professionalId = null) => {
  // Usar a função getSalonId como fallback se salonId for indefinido
  const effectiveSalonId = salonId || getSalonId();
  
  if (!effectiveSalonId) {
    console.error('[getTimeBlockings] salonId é obrigatório');
    return [];
  }
  
  try {
    // Criar chave de cache para esta consulta
    const cacheKey = ['timeBlockings', effectiveSalonId, date, professionalId];
    
    // Verificar se já temos os dados em cache
    const cachedData = queryClient.getQueryData(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    // Delegar para a implementação no Firebase
    const blockings = await getTimeBlockingsFromFirebase(effectiveSalonId, date, professionalId);
    
    // Salvar em cache
    queryClient.setQueryData(cacheKey, blockings);
    
    return blockings;
  } catch (error) {
    console.error('[availability.js] Erro ao buscar bloqueios de horário:', error);
    return [];
  }
}; 